import * as THREE from 'three';
import {
  LegacyWhiteStarBurstEffect,
  parseLegacyPetPointParticleConfig,
  type LegacyPetPointParticleConfig,
} from './LegacyPetPointFeedback';

const EFFECT_ASSET_ROOT = '/legacy/world/pets/effects';

export const LEGACY_BUILDING_READY_SOUND = '/legacy/sound/misc/boing.ogg';
export const LEGACY_BUILDING_READY_DISTANCE = 15;

export type LegacyBuildingReadyFeedbackInput = Readonly<{
  wasReady: boolean | undefined;
  ready: boolean;
  placedOnMap: boolean;
  loaded: boolean;
  clientUptimeMilliseconds: number;
  playerDistanceSquared: number | undefined;
}>;

/** Applies the visibility and proximity guards after a production building becomes ready. */
export function legacyBuildingReadyFeedbackEligible(
  input: LegacyBuildingReadyFeedbackInput,
): boolean {
  if (
    input.wasReady === true ||
    !input.ready ||
    !input.placedOnMap ||
    !input.loaded ||
    input.clientUptimeMilliseconds <= 100
  ) {
    return false;
  }
  return (
    input.playerDistanceSquared === undefined ||
    input.playerDistanceSquared <= LEGACY_BUILDING_READY_DISTANCE ** 2
  );
}

/**
 * Source `WE.d()` attaches the particle root at the building root translation, then raises the
 * emitter by two thirds of the base model's y half-extent (`height / 3`).
 */
export function legacyBuildingReadyFeedbackPosition(
  buildingAnchor: THREE.Vector3,
  buildingWorldBounds: THREE.Box3,
): THREE.Vector3 {
  const position = buildingAnchor.clone();
  if (buildingWorldBounds.isEmpty()) return position;
  const height = buildingWorldBounds.max.y - buildingWorldBounds.min.y;
  if (Number.isFinite(height) && height > 0) position.y += height / 3;
  return position;
}

export type LegacyWhiteStarBurstIdentity = Readonly<{
  rootName: string;
  particleNamePrefix: string;
}>;

export class LegacyWhiteStarBurstLibrary {
  private config: Promise<LegacyPetPointParticleConfig> | undefined;
  private texture: Promise<THREE.Texture> | undefined;
  private disposed = false;

  async create(
    targetPosition: THREE.Vector3,
    identity?: LegacyWhiteStarBurstIdentity,
  ): Promise<LegacyWhiteStarBurstEffect> {
    const [config, texture] = await Promise.all([this.loadConfig(), this.loadTexture()]);
    if (this.disposed) throw new Error('White-star burst library is disposed');
    return new LegacyWhiteStarBurstEffect(config, texture, targetPosition, Math.random, identity);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.texture !== undefined) void this.texture.then((texture) => texture.dispose());
    this.config = undefined;
    this.texture = undefined;
  }

  private loadConfig(): Promise<LegacyPetPointParticleConfig> {
    this.config ??= fetch(`${EFFECT_ASSET_ROOT}/pet-point-particles.json`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Building-ready particle request failed with ${String(response.status)}`);
        }
        return response.json() as Promise<unknown>;
      })
      .then(parseLegacyPetPointParticleConfig);
    return this.config;
  }

  private loadTexture(): Promise<THREE.Texture> {
    this.texture ??= new THREE.TextureLoader()
      .loadAsync(`${EFFECT_ASSET_ROOT}/whitestar.jpg`)
      .then((texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        return texture;
      });
    return this.texture;
  }
}

/** Compatibility name for the production-ready building caller. */
export class LegacyBuildingReadyFeedback extends LegacyWhiteStarBurstLibrary {}
